import {
  EXPORT_ERROR_MESSAGE,
  type TableColumn,
  type TableRow,
} from '@data/components/basic/Table/types';
import type { useMetricsTableV2Api } from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import type { ProductCode } from '@data/main';
import { exportTableToCSV } from '@data/utils/export-table-to-csv';
import { filterTableData } from '@data/utils/filter-table-data';
import formatDate from '@data/utils/format-date';
import type { ColumnRule } from '@data/utils/resolve-columns';
import { useCallback } from 'react';

export function useExportToCSVHandler(
  refetchExportData: ReturnType<typeof useMetricsTableV2Api>['refetch'],
  tableColumns: ColumnRule[] | undefined,
  edApp: ProductCode,
  reportPath: string | undefined,
  /** Reshapes the exported table, e.g. to add a computed column. */
  transform?: (
    columns: TableColumn[],
    rows: TableRow[]
  ) => { columns: TableColumn[]; rows: TableRow[] }
) {
  return useCallback(async () => {
    const { data: exportData, error } = await refetchExportData();
    if (error) throw error;
    if (!exportData) throw new Error(EXPORT_ERROR_MESSAGE);

    const { visibleColumns, visibleRows } = filterTableData(
      exportData,
      tableColumns,
      edApp
    );
    const exported = transform?.(visibleColumns, visibleRows) ?? {
      columns: visibleColumns,
      rows: visibleRows,
    };

    exportTableToCSV(
      exported.columns,
      exported.rows,
      `${reportPath ?? 'report'}_${edApp}_${formatDate(new Date())}.csv`
    );
  }, [refetchExportData, tableColumns, edApp, reportPath, transform]);
}
